import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';

import { LessonLimitPaywall } from '../../components/_molecules/LessonLimitPaywall/LessonLimitPaywall';
import useAccessBadge, {
  LicenseCoarseLevels,
  UserAccessBadge,
} from '../../hooks/useAccessBadge';
import useAttioEventTracking from '../../hooks/useAttioEventTracking';
import useTeachingCreditState from '../../hooks/useTeachingCreditState';
import { useMetrics } from '../../hooks/useMetrics';
import { TeachingCreditState } from '../../services/backendService/backendService';

interface PaywallContextType {
  promptPaywall: (source: string, message?: React.ReactNode) => void;
  showCreditInfo: (source: string) => void;
  hidePaywall: () => void;
  isShowingPaywall: boolean;
  shouldPromptPaywall: () => boolean;
  isReady: boolean;
  userAccessBadge: UserAccessBadge | undefined;
  hasReachedLessonLimit: boolean | undefined;
  teachingCreditState: TeachingCreditState | undefined;
  remaining: number | undefined;
  isLessonLimitEnabled: boolean;
  isLessonLimitLoading: boolean;
}

const PaywallContext = createContext<PaywallContextType | undefined>(undefined);

export const PaywallProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { accessBadge: userAccessBadge, isFetched } = useAccessBadge();
  const {
    creditState: teachingCreditState,
    hasReachedLimit: hasReachedLessonLimit,
    remaining,
    isEnabled: isLessonLimitEnabled,
    isLoading: isLessonLimitLoading,
  } = useTeachingCreditState();
  const [isShowingPaywall, setIsShowingPaywall] = useState(false);
  const metrics = useMetrics();
  const { trackEvent } = useIntercom();
  const trackAttioEvent = useAttioEventTracking();

  const shouldPromptPaywall = useCallback(() => {
    if (!isFetched || !userAccessBadge) return false;
    if (!isLessonLimitEnabled) return false;
    return (
      !!hasReachedLessonLimit &&
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
    );
  }, [isFetched, userAccessBadge, isLessonLimitEnabled, hasReachedLessonLimit]);

  const promptPaywall = useCallback(
    (source: string, _message?: React.ReactNode) => {
      if (shouldPromptPaywall()) {
        const eventProps = {
          source,
          used: teachingCreditState?.used,
          remaining,
          weeklyRate: teachingCreditState?.weeklyRate,
          bonusRemaining: teachingCreditState?.bonusRemaining,
          plan: userAccessBadge?.license?.coarseLevel,
          subPlan: userAccessBadge?.license?.fineLevel,
          organizationId: userAccessBadge?.organization?.id,
        };

        metrics.logEvent('TeachingSessions.StartBlocked', eventProps);
        trackEvent('teaching_sessions_start_blocked', eventProps);
        trackAttioEvent('paywall_blocked', {
          organizationId: userAccessBadge?.organization?.id,
          userRole: userAccessBadge?.user?.profileInfo?.role,
          email: userAccessBadge?.user?.email,
          firstName: userAccessBadge?.user?.firstName,
          lastName: userAccessBadge?.user?.lastName,
          source,
        });

        setIsShowingPaywall(true);
      }
    },
    [
      shouldPromptPaywall,
      teachingCreditState,
      remaining,
      userAccessBadge,
      metrics,
      trackEvent,
      trackAttioEvent,
    ],
  );

  /**
   * Show the credit info / paywall modal regardless of whether the limit is reached.
   * Used by the designer badge so users can always click to see their credit state.
   */
  const showCreditInfo = useCallback(
    (source: string) => {
      if (!isLessonLimitEnabled) return;

      metrics.logEvent('CreditInfo.Opened', {
        source,
        hasReachedLessonLimit,
        remaining,
        plan: userAccessBadge?.license?.coarseLevel,
        organizationId: userAccessBadge?.organization?.id,
      });

      setIsShowingPaywall(true);
    },
    [isLessonLimitEnabled, metrics, hasReachedLessonLimit, remaining, userAccessBadge],
  );

  const hidePaywall = useCallback(() => {
    metrics.logEvent('TeachingSessions.PaywallDismissed', {
      hasReachedLessonLimit,
      remaining,
      plan: userAccessBadge?.license?.coarseLevel,
      organizationId: userAccessBadge?.organization?.id,
    });
    setIsShowingPaywall(false);
  }, [metrics, hasReachedLessonLimit, remaining, userAccessBadge]);

  const contextValue = useMemo<PaywallContextType>(
    () => ({
      promptPaywall,
      showCreditInfo,
      hidePaywall,
      isShowingPaywall,
      shouldPromptPaywall,
      isReady: isFetched,
      userAccessBadge,
      hasReachedLessonLimit,
      teachingCreditState,
      remaining,
      isLessonLimitEnabled,
      isLessonLimitLoading,
    }),
    [
      promptPaywall,
      showCreditInfo,
      hidePaywall,
      isShowingPaywall,
      shouldPromptPaywall,
      isFetched,
      userAccessBadge,
      hasReachedLessonLimit,
      teachingCreditState,
      remaining,
      isLessonLimitEnabled,
      isLessonLimitLoading,
    ],
  );

  return (
    <PaywallContext.Provider value={contextValue}>
      {children}
      <LessonLimitPaywall
        visible={isShowingPaywall}
        onClose={hidePaywall}
        creditState={teachingCreditState}
        region={userAccessBadge?.organization?.region}
        country={userAccessBadge?.organization?.country}
      />
    </PaywallContext.Provider>
  );
};

export const usePaywall = (): PaywallContextType => {
  const context = useContext(PaywallContext);
  if (context === undefined) {
    throw new Error('usePaywall must be used within a PaywallProvider');
  }
  return context;
};
